import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "../lib/adminApi";
import { Field, TextInput, Checkbox, NumberInput } from "./formKit";
import { Trash2, Upload, ImageIcon, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HeroSlidesSection() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [newCaption, setNewCaption] = useState("");
  const [newAlt, setNewAlt] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/hero-slides");
      setSlides(data.items || []);
    } catch {
      toast.error("Couldn't load hero slides.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setSlide = (id, patch) =>
    setSlides((arr) => arr.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose a photo first (JPG, PNG or WEBP).");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large — maximum 4MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", newCaption);
      fd.append("alt_text", newAlt);
      fd.append("active", "true");
      fd.append("sort_order", String(slides.length));
      await adminApi.post("/admin/hero-slides", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded.");
      setNewCaption("");
      setNewAlt("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const saveSlide = async (s) => {
    setSavingId(s.id);
    try {
      const fd = new FormData();
      fd.append("caption", s.caption || "");
      fd.append("alt_text", s.alt_text || "");
      fd.append("active", String(!!s.active));
      fd.append("sort_order", String(s.sort_order ?? 0));
      await adminApi.put(`/admin/hero-slides/${s.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Slide saved.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save slide.");
    } finally {
      setSavingId(null);
    }
  };

  const removeSlide = async (id) => {
    if (!window.confirm("Delete this slideshow photo?")) return;
    try {
      await adminApi.delete(`/admin/hero-slides/${id}`);
      toast.success("Slide deleted.");
      setSlides((arr) => arr.filter((s) => s.id !== id));
    } catch {
      toast.error("Couldn't delete slide.");
    }
  };

  return (
    <section
      className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4"
      data-testid="homepage-hero-slideshow-section"
    >
      <h3 className="font-display text-lg font-bold tracking-tight">
        Homepage Hero Slideshow
      </h3>
      <p className="text-sm text-slate-500 -mt-2">
        Photos shown in the right-side hero visual on the homepage. Supports up
        to 10 photos (1–4 recommended); auto-rotates every 5 seconds when more
        than one is active. JPG, PNG or WEBP, max 4MB each.
      </p>

      {/* Upload new */}
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-4 grid gap-3">
        <span className="eyebrow text-slate-600">Add a photo</span>
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          className="text-sm text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-xs file:font-bold file:text-white hover:file:bg-slate-700"
          data-testid="hero-slide-file-input"
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Caption (optional)" hint="Short text shown over the photo.">
            <TextInput
              value={newCaption}
              onChange={setNewCaption}
              placeholder="Hands-on robotics class"
              data-testid="hero-slide-new-caption"
            />
          </Field>
          <Field label="Alt text (optional)" hint="Describes the photo for screen readers.">
            <TextInput
              value={newAlt}
              onChange={setNewAlt}
              placeholder="Children building a robot together"
              data-testid="hero-slide-new-alt"
            />
          </Field>
        </div>
        <button
          type="button"
          onClick={upload}
          disabled={uploading}
          className="inline-flex items-center gap-2 self-start rounded-full bg-[#FF8C42] px-5 py-2.5 text-xs font-bold text-white disabled:opacity-50"
          data-testid="hero-slide-upload-btn"
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? "Uploading…" : "Upload photo"}
        </button>
      </div>

      {/* Existing slides */}
      {loading ? (
        <p className="text-sm text-slate-500">Loading slides…</p>
      ) : slides.length === 0 ? (
        <p
          className="text-sm text-slate-500 flex items-center gap-2"
          data-testid="hero-slides-empty"
        >
          <ImageIcon className="h-4 w-4" /> No photos yet — the homepage shows a
          soft Nexora watermark until you add one.
        </p>
      ) : (
        <div className="grid gap-3">
          {slides.map((s, idx) => (
            <div
              key={s.id}
              className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 grid gap-3 sm:grid-cols-[128px_1fr] items-start"
              data-testid={`hero-slide-row-${idx}`}
            >
              <img
                src={`${BACKEND_URL}${s.image_url}`}
                alt={s.alt_text || "Slide preview"}
                className="h-24 w-32 rounded-xl object-cover border border-slate-200 bg-white"
                data-testid={`hero-slide-preview-${idx}`}
              />
              <div className="grid gap-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Caption">
                    <TextInput
                      value={s.caption || ""}
                      onChange={(v) => setSlide(s.id, { caption: v })}
                      data-testid={`hero-slide-caption-${idx}`}
                    />
                  </Field>
                  <Field label="Alt text">
                    <TextInput
                      value={s.alt_text || ""}
                      onChange={(v) => setSlide(s.id, { alt_text: v })}
                      data-testid={`hero-slide-alt-${idx}`}
                    />
                  </Field>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <Checkbox
                    value={!!s.active}
                    onChange={(v) => setSlide(s.id, { active: v })}
                    label="Active"
                    data-testid={`hero-slide-active-${idx}`}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600">Sort order</span>
                    <div className="w-20">
                      <NumberInput
                        value={s.sort_order ?? 0}
                        onChange={(v) => setSlide(s.id, { sort_order: v })}
                        data-testid={`hero-slide-sort-${idx}`}
                      />
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => saveSlide(s)}
                      disabled={savingId === s.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                      data-testid={`hero-slide-save-${idx}`}
                    >
                      <Save className="h-3.5 w-3.5" />
                      {savingId === s.id ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSlide(s.id)}
                      className="text-slate-400 hover:text-red-600"
                      aria-label="Delete slide"
                      data-testid={`hero-slide-delete-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
